import { FC, useMemo } from 'react';
import CustomListItem from 'components/List/ListItem/ListItem';
import { StatusChip } from 'components/Dropdown/AssigneeDropdown/listItems/utils';

import { StyledName } from './style';
import { PEOPLE } from '../../constants';
import { Box, Stack, Typography } from '@mui/material';
import { useLoggedInUser } from 'customhooks/index';
import UserAvatar from 'components/Avatar';
import TeamAvatar from 'components/Avatar/TeamAvatar/TeamAvatar.cmp';
import LocationIcon from 'components/Icons/locationIcon';
import { useSelector } from 'react-redux';
import selectors from 'store/selectors';

const ListItem = ({
  option,
  selected,
  changeHandler,
  customListProps,
  startIcon,
  // endIconRenderer,
}) => (
  <CustomListItem
    checkbox={customListProps?.isMulti}
    styleSelected={
      !customListProps?.isMulti && customListProps?.showInlineSelection
    }
    key={option.id}
    id={option.id}
    selected={selected?.includes(option?.id)}
    onClick={(e) => {
      changeHandler(e, option?.id);
    }}
    style={{ borderRadius: 6, padding: '0px 10px' }}
    startIcon={startIcon}
    checkboxProps={{
      style: {
        width: 18,
        height: 18,
      },
    }}
    // endIcon={endIconRenderer}
  >
    <StyledName>{option.label}</StyledName>
  </CustomListItem>
);

// Default Props
const defaultProps = {
  isMulti: true,
  showInlineSelection: false,
  selectedListPlaceholder: 'Selected',
  unselectedListPlaceholder: 'Suggested',
  partition: false,
};

const SuggestedList: FC<any> = ({
  listProps = {},
  allListProps,
  selected,
  onSelectionCallback,
  displayOptions,
}) => {
  const user = useLoggedInUser();
  const customListProps = { ...defaultProps, ...listProps };

  const userInfo = useSelector(selectors.getUserInfoState);
  const { locations, role, teams } = userInfo.content;

  const suggestedOptions = [
    {
      id: 'ALL_MY_TASKS',
      label: `${user?.fullName} (All my tasks, includes my role's tasks)`,
      startIcon: (
        <UserAvatar
          avatarSx={{ fontWeight: 800 }}
          width={28}
          height={28}
          userId={user?.id}
        />
      ),
      subTitle: `${user?.fullName} (All my tasks, includes my role's tasks)`,
      value: `${user?.fullName} (All my tasks, includes my role's tasks)`,
    },
    {
      id: 'MY_LOCATIONS',
      label: `My Locations (All assigned to my locations)`,
      startIcon: (
        <Box
          sx={{
            width: '28px',
            height: '28px',
            borderRadius: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            background: '#6868FE',
            '& path': {
              stroke: 'currentColor',
              strokeWidth: 1.5,
              strokeOpacity: 1,
            },
          }}
        >
          <LocationIcon filled={false} />
        </Box>
      ),
      subTitle: `My Locations (All assigned to my locations)`,
      value: `My Locations (All assigned to my locations)`,
    },
    {
      id: 'MY_TEAMS',
      label: `My Teams (All assigned to my teams)`,
      startIcon: <TeamAvatar width={28} height={28} teamId={'all'} />,
      subTitle: `My Teams (All assigned to my teams)`,
      value: `My Teams (All assigned to my teams)`,
    },
  ];

  // Check if all of user's teams, locations, or tasks are already selected
  const isAllTasksSelected = useMemo(() => {
    return (
      selected?.includes(user?.id) &&
      (role?.data ? selected?.includes(role?.data) : true)
    );
  }, [selected, user?.id, role?.id]);

  const isAllLocationsSelected = useMemo(() => {
    if (!selected || !locations || locations?.data?.length === 0) return false;
    return locations?.data?.every((location) => selected?.includes(location));
  }, [selected, locations]);

  const isAllTeamsSelected = useMemo(() => {
    if (!teams || teams?.data?.length === 0) return false;
    return teams?.data.every((team) => selected?.includes(team));
  }, [selected, teams]);

  // Add selected ids to include the suggested options when all items in that category are selected
  const allSelectedIds = useMemo(() => {
    const ids = [...(selected || [])];
    if (isAllTasksSelected) {
      ids.push('ALL_MY_TASKS');
    }
    if (isAllLocationsSelected) {
      ids.push('MY_LOCATIONS');
    }
    if (isAllTeamsSelected) {
      ids.push('MY_TEAMS');
    }
    return ids;
  }, [
    selected,
    isAllTasksSelected,
    isAllLocationsSelected,
    isAllTeamsSelected,
  ]);

  const onChangeHandler = (e, optionId) => {
    if (optionId === 'ALL_MY_TASKS') {
      // Check if already selected
      if (isAllTasksSelected) {
        // Remove user and role from selection
        const selectedPeoples = allListProps?.people?.selected
          ? allListProps.people.selected.filter((id) => id !== user?.id)
          : [];

        const selectedRoles = allListProps?.roles?.selected
          ? allListProps.roles.selected.filter((id) => id !== role?.data)
          : [];

        onSelectionCallback({
          currentSelected: null,
          selectedList: {
            ...(displayOptions?.people && {
              people: selectedPeoples,
            }),
            ...(displayOptions?.teams && {
              teams: allListProps?.teams?.selected,
            }),
            ...(displayOptions?.locations && {
              locations: allListProps?.locations?.selected,
            }),
            ...(displayOptions?.roles && {
              roles: selectedRoles,
            }),
          },
          all: false,
        });
      } else {
        // Add user and role to selection
        const selectedPeoples = allListProps?.people?.selected
          ? [...allListProps.people.selected, user?.id]
          : [user?.id];

        const selectedRoles =
          allListProps?.roles?.selected && role?.data
            ? [...allListProps.roles.selected, role?.data]
            : role?.data
            ? [role?.data]
            : [];

        onSelectionCallback({
          currentSelected: null,
          selectedList: {
            ...(displayOptions?.people && {
              people: selectedPeoples,
            }),
            ...(displayOptions?.teams && {
              teams: allListProps?.teams?.selected,
            }),
            ...(displayOptions?.locations && {
              locations: allListProps?.locations?.selected,
            }),
            ...(displayOptions?.roles && {
              roles: selectedRoles,
            }),
          },
          all: false,
        });
      }
      return;
    }

    if (optionId === 'MY_LOCATIONS') {
      if (isAllLocationsSelected) {
        // Remove all user locations from selection
        const selectedLocations = allListProps?.locations?.selected
          ? allListProps.locations.selected.filter(
              (id) => !locations.data.includes(id),
            )
          : [];

        onSelectionCallback({
          currentSelected: null,
          selectedList: {
            ...(displayOptions?.people && {
              people: allListProps?.people?.selected,
            }),
            ...(displayOptions?.teams && {
              teams: allListProps?.teams?.selected,
            }),
            ...(displayOptions?.locations && {
              locations: selectedLocations,
            }),
            ...(displayOptions?.roles && {
              roles: allListProps?.roles?.selected,
            }),
          },
          all: false,
        });
      } else if (locations && locations?.data?.length > 0) {
        // Add all user locations to selection
        const selectedLocations = allListProps?.locations?.selected
          ? [
              ...new Set([
                ...allListProps.locations.selected,
                ...locations.data,
              ]),
            ]
          : [...locations.data];

        onSelectionCallback({
          currentSelected: null,
          selectedList: {
            ...(displayOptions?.people && {
              people: allListProps?.people?.selected,
            }),
            ...(displayOptions?.teams && {
              teams: allListProps?.teams?.selected,
            }),
            ...(displayOptions?.locations && {
              locations: selectedLocations,
            }),
            ...(displayOptions?.roles && {
              roles: allListProps?.roles?.selected,
            }),
          },
          all: false,
        });
      }
      return;
    }

    if (optionId === 'MY_TEAMS') {
      if (isAllTeamsSelected) {
        // Remove all user teams from selection
        const selectedTeams = allListProps?.teams?.selected
          ? allListProps.teams.selected.filter((id) => !teams.data.includes(id))
          : [];

        onSelectionCallback({
          currentSelected: null,
          selectedList: {
            ...(displayOptions?.people && {
              people: allListProps?.people?.selected,
            }),
            ...(displayOptions?.teams && {
              teams: selectedTeams,
            }),
            ...(displayOptions?.locations && {
              locations: allListProps?.locations?.selected,
            }),
            ...(displayOptions?.roles && {
              roles: allListProps?.roles?.selected,
            }),
          },
          all: false,
        });
      } else if (teams && teams?.data?.length > 0) {
        // Add all user teams to selection
        const selectedTeams = allListProps?.teams?.selected
          ? [...new Set([...allListProps.teams.selected, ...teams.data])]
          : [...teams.data];

        onSelectionCallback({
          currentSelected: null,
          selectedList: {
            ...(displayOptions?.people && {
              people: allListProps?.people?.selected,
            }),
            ...(displayOptions?.teams && {
              teams: selectedTeams,
            }),
            ...(displayOptions?.locations && {
              locations: allListProps?.locations?.selected,
            }),
            ...(displayOptions?.roles && {
              roles: allListProps?.roles?.selected,
            }),
          },
          all: false,
        });
      }
      return;
    }
  };
  return (
    <Stack>
      <Typography fontWeight={'600'} fontSize={'13px'} color="#616161">
        Suggested
      </Typography>
      <Stack>
        {suggestedOptions?.map((option, _) => {
          const { startIcon } = option;

          return (
            <ListItem
              key={option.id}
              option={option}
              selected={allSelectedIds}
              changeHandler={onChangeHandler}
              customListProps={customListProps}
              startIcon={startIcon}
            />
          );
        })}
      </Stack>
    </Stack>
  );
};

export default SuggestedList;
